<template lang="pug">
.wrap
	.container.section(v-if="songDetail")
		client-only
			LayoutBackButton
		SongDetailHeader.song-header(:song="songDetail")
		.more-songs-wrap(v-if="songDetail.geoblocked && showGeoBlocked && geoBlockedSuggestions?.results?.length")
			.columns
				.column.col-12
					p.more-songs-title {{ t('songDetail.geoblockedSuggestions') }}
			ol.columns.is-multiline
				SongListItem.column.is-6-mobile-small.is-4-mobile.is-3-tablet.is-2-desktop.is-2-widescreen.is-2-fullhd(
					v-for="(song, i) in geoBlockedSuggestions.results"
					:key="i"
					layout="column"
					:song="song"
				)
		.more-songs-wrap(v-else-if="response?.results?.length")
			.columns
				.column.col-12
					p.more-songs-title {{ t('artists.moreFrom') }} {{ songDetail.artists[0].name }}
			ol.columns.is-multiline.more-songs(v-if="$viewport.isGreaterOrEquals('desktop')")
				SongListItem.song-item.column.is-6-mobile-small.is-4-mobile.is-3-tablet.is-2-desktop.is-2-widescreen.is-2-fullhd(v-for="(song, i) in response.results" :key="i" layout="column" :song="song")
				.column.is-6-mobile-small.is-4-mobile.is-3-tablet.is-2-desktop.is-2-widescreen.is-2-fullhd(v-if="response.results?.length >= 5")
					NuxtLink(:to="localePath(`/artists/${songDetail.artists[0].slug}/${songDetail.artists[0].hash}`)")
						ListSeeMore
			SongListItemRowWrap(v-else)
				template(v-slot:songs)
					ol
						SongListItem(v-for="(song, i) in response.results" :key="i" :song="song")
		.info-wrapper.columns.is-mobile.is-multiline(v-if="!songDetail.geoblocked")
			.column.is-full-mobile.is-half-tablet.is-two-thirds-desktop.lyrics-column
				.lyrics(v-if="lyrics")
					h2
						span.lyrics-title {{ t('song.lyrics.title') }}
						span.song-title {{ songDetail.name }}
					.lyrics-text
						article
							p(v-for="(line, index) in lyrics" :key="`${index}-text`") {{ line }}
			.column.is-full-mobile.is-half-tablet.is-one-third-desktop.credits-column(v-if="credits && Object.keys(credits).length > 0")
				SongCredits.credits(:credits="credits" :standalone="true")
</template>

<script setup lang="ts">
import type { LoadSongsResult, Song } from '~~/types'

const { setMetaTitle, setMetaDescription, setMetaImg, setSongMetaTags } = useMetaTags()
const { getSongObject, getParsedSongLyrics, loadArtistSongs } = useSongs()
const config = useRuntimeConfig()
const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const { $viewport, $singaApi } = useNuxtApp()

const geolocationStore = useGeoLocationStore()
const { contentMarket } = storeToRefs(geolocationStore)

const defaultSongResult: LoadSongsResult = {
	results: [],
	nextPage: false,
	count: 0,
	page: 1
}

const { data: songDetail, refresh: refreshSongDetail } = await useAsyncData<Song>(
	`song_detail_${route.params.slug}_${route.params.songSlug}_${route.params.hash}_${contentMarket.value?.market}`,
	async () => {
		const result = await getSongObject(
			route.params.slug.toString(),
			route.params.songSlug.toString(),
			route.params.hash.toString()
		)
		if (!result) {
			throw createError({
				statusCode: 404
			})
		}
		return result
	}
)

const { data: lyrics } = await useAsyncData<string[] | null>(
	`parsed_lyrics_${songDetail.value?.id}_${songDetail.value?.variants[0].id}_${contentMarket.value?.market}`,
	() => getParsedSongLyrics(songDetail.value!.id, songDetail.value!.variants[0].id)
)

const { data: response, refresh: refreshArtistSongs } = await useAsyncData<LoadSongsResult>(
	`artist_songs_${songDetail.value?.artists[0].id}`,
	async () => {
		try {
			if (songDetail.value?.artists[0]?.id) {
				const result = await loadArtistSongs(songDetail.value.artists[0].id)
				if (result) {
					result.results = result.results
						.filter(song => song.id !== songDetail.value?.id)
						.slice(0, 5)
				}
				return result || defaultSongResult
			}
			return defaultSongResult
		} catch (error) {
			console.error('Failed to fetch artist songs:', error)
			return defaultSongResult
		}
	},
	{
		default: () => defaultSongResult
	}
)

const { data: geoBlockedSuggestions, refresh: refreshGeoBlockedSuggestions } = await useAsyncData<LoadSongsResult>(
	`geoblocked_suggestions_${songDetail.value?.id}_${contentMarket.value?.market}`,
	async () => {
		if (!songDetail.value?.geoblocked) return defaultSongResult
		try {
			const { loadSongs } = useSonglists({
				songlistApiResource: $singaApi.Songs.list,
				params: { page: 1, page_size: 5, sort: 'top' }
			})
			return await loadSongs({ page: 1, page_size: 5, sort: 'top' }, undefined, true) || defaultSongResult
		} catch (error) {
			console.error('Failed to fetch geoblocked suggestions:', error)
			return defaultSongResult
		}
	},
	{
		default: () => defaultSongResult
	}
)

const showGeoBlocked = ref(songDetail.value?.geoblocked || false)

// Set up credits if available
const credits = computed(() => {
	if (!songDetail.value?.geoblocked && songDetail.value?.copyrights) {
		const creditsMap = songDetail.value.copyrights.reduce((acc, copyright) => {
			if (!acc[copyright.role]) {
				acc[copyright.role] = []
			}
			acc[copyright.role].push(copyright.name)
			return acc
		}, {} as Record<string, string[]>)

		return Object.keys(creditsMap).reduce((acc, role) => {
			acc[role] = creditsMap[role].join(', ')
			return acc
		}, {} as Record<string, string>)
	}
	return null
})

const { getAllArtists } = useArtistNames(songDetail.value?.artists || [])

// Set up meta tags
const mainTitle = computed(() => `${getAllArtists()} - ${songDetail.value?.name}`)
const pageTitle = computed(() => `${mainTitle.value} Karaoke`)

useHead({
	title: pageTitle.value,
	meta: [
		...setMetaTitle(pageTitle.value),
		...setMetaDescription(`${t('metaDescription.song', { title: mainTitle.value, count: config.public.catalogCount })}`),
		...setMetaImg(
			songDetail.value?.image?.medium?.url || '',
			songDetail.value?.image?.medium?.width || 0,
			songDetail.value?.image?.medium?.height || 0
		),
		...setSongMetaTags({
			preview: songDetail.value?.variants[0]?.preview || '',
			duration: songDetail.value?.duration.toString() || '',
			artists: songDetail.value?.artists || []
		})
	]
})

watch(contentMarket, (newVal, oldVal) => {
	if (newVal.market?.toLowerCase() !== oldVal.market?.toLowerCase()) {
		refreshSongDetail()
		refreshArtistSongs()
		refreshGeoBlockedSuggestions()
	}
})
</script>

<style lang="sass" scoped>
.back-button
	margin-top: $spacing-24
.song-header
	margin-top: $spacing-16
	padding-bottom: $spacing-24
	@media (min-width: $tablet)
		margin-top: $spacing-32
		padding-bottom: $spacing-64

.song-songs
	margin: $spacing-24 0 $spacing-80

.more-songs-title
	@include font(basier, bold)
	@include fontSize(l)
	color: $color-grey-30

.modal-card
	width: 100%
	padding: $spacing-4 $spacing-48 $spacing-48
.modal-card-head
	margin-bottom: 12px
.columns.is-multiline
	display: flex
.more-songs-wrap
	padding-bottom: 74px
.info-wrapper
	margin-bottom: $spacing-80

.lyrics-title
	@include font(basier, bold)
	@include fontSize(l)
	color: $color-grey-30
	display: block

.song-title
	@include font(basier, regular)
	@include fontSize(m)
	color: $color-grey-60
	margin-bottom: 12px

.lyrics
	display: flex
	flex-direction: column
	background-color: #0b0a0a
	padding: $spacing-24 $spacing-32 $spacing-24 $spacing-32
	border-radius: $radius-default
.lyrics-text
	@include fontSize(s)
	@include font(basier, regular)
	color: $color-grey-30
	white-space: pre-wrap
	line-height: $spacing-32 !important
	z-index: 1
	margin-top: $spacing-16
	padding-right: $spacing-4
	@media (min-width: $tablet)
		overflow-y: scroll
		margin-top: 0
		&:before, &:after
			content: ''
			position: sticky
			height: $spacing-32
			display: block
			z-index: 0
			pointer-events: none
		&:before
			margin-top: -$spacing-16
			top: 0
			background: linear-gradient(#0b0a0a, transparent)
		&:after
			bottom: 0
			background: linear-gradient(transparent, #0b0a0a)

		&::-webkit-scrollbar
			background: $transparent-white-4
			width: $spacing-8
		&::-webkit-scrollbar-thumb
			background: $transparent-white-8
			border-radius: $radius-default
		&::-webkit-scrollbar-track
			border-radius: $radius-default

.credits
	background-color: #0b0a0a
	padding: $spacing-24 $spacing-32 $spacing-24 $spacing-32
	border-radius: $radius-default
	height: auto

.lyrics
	@media (min-width: $tablet)
		height: 540px
.error-button
	margin-top: $spacing-24

.more-songs
	.song-item
		display: none
		&:nth-child(-n+1)
			display: block
	@media (min-width: $mobile-small)
		.song-item:nth-child(-n+2)
			display: block
	@media (min-width: $tablet)
		.song-item:nth-child(-n+3)
			display: block
	@media (min-width: $desktop)
		.song-item:nth-child(-n+5)
			display: block

.info-wrapper
	display: flex
	flex-direction: column
	.column
		&.lyrics-column
			order: 2
		&.credits-column
			order: 1
	@media (min-width: $tablet)
		flex-direction: row
		.column
			&.lyrics-column
				order: 1
			&.credits-column
				order: 2
</style>
